import { api } from './api';
import { bildBericht } from './bildbericht';
import { takte } from './bildtakt';
import { GL_GEWUENSCHT, leinwandBericht, messungZurueck } from './glmaler';
import { bilder, bilderZuruecksetzen } from './messung';
import { vorladeStand } from './vorladen';
import { bildWartenBericht, pufferBericht } from './puffer';
import { ramStand } from './ramvorrat';
import { filmLaeuft } from './filmwahl';
import { useEditor } from './store';

/**
 * Der Schnittplatz schickt seine Zahlen selbst (7.69j).
 *
 * Fünf Reparaturen auf Verdacht an einem Tag, und Sascha musste jedes Mal
 * sagen, ob es geholfen hat -- „du musst dir das besser anschauen“. Recht
 * hat er. Der Prüfstand hier dekodiert kein H.264, und das iPhone steht
 * nicht daneben. Also schickt der Schnittplatz beim Abspielen alle zehn
 * Sekunden, und beim Anhalten nach mindestens einer Sekunde, einen Befund an
 * den Server -- dieselben Zahlen, die das Melde-Blatt zeigt, nur ohne dass
 * jemand tippen muss. Zu lesen unter `/api/diagnose/messwerte`.
 *
 * Technische Zahlen, kein Inhalt: wie oft gezeichnet und hochgeladen wurde,
 * wie viele Bilder das Gerät ausgelassen hat, wie oft ein Video im Tempo
 * nachgestellt oder gesprungen ist, und die Browser-Kennung. Nach jedem
 * Befund fangen die Zähler von vorn an, damit jeder Befund ein Fenster ist.
 */

const ALLE = 10_000;
const MINDESTENS = 1_000;

let fensterSeit = 0;
/**
 * Wo der Kopf am Anfang des Fensters stand, und wie viele Bilder jede Fläche
 * bis dahin dekodiert hatte (7.69p). Ohne diese zwei Zahlen sagt ein Befund
 * nicht, ob es in Echtzeit lief: „Element steht bei 1,86 s“ ist wenig wert,
 * „in 10 s kamen 1,9 s Film und 19 Bilder“ ist eine Diagnose.
 */
let kopfSeit = 0;
let bilderSeit: Record<string, number> = {};
let laeuftSeit = 0;
let zeitgeber = 0;
/**
 * Wie lange es vom Druck auf Abspielen bis zum ersten Millimeter Bewegung
 * dauert (7.69v).
 *
 * Bis zum 13. September steckte diese Zeit *im* Tempo: das Fenster begann
 * beim Drücken, und was der Anlauf verschluckte, sah aus wie „läuft zu
 * langsam“. Der Befund vom 12. September sagte darum `0,89-fach` für den
 * vorgerechneten Film und `0,98-fach` für einen Clip -- und ich hatte daraus
 * geschlossen, der Film laufe elf Prozent zu langsam. Das war ein Schluss,
 * keine Messung.
 *
 * Nachgerechnet fehlen dem Film 0,35 s und einem Clip 0,12--0,22 s, und zwar
 * unabhängig von der Fensterlänge. Das passt zu einem *Anlauf*, der beim
 * Film länger ist, genauso gut wie zu langsamem Lauf -- unterscheiden lässt
 * es sich nur, wenn man beides getrennt misst. Genau das steht hier: der
 * Anlauf wird gestoppt, und das Tempo zählt erst ab der ersten Bewegung.
 *
 * `null` heisst „noch keine Bewegung gesehen“ -- und das ist selbst ein
 * Befund: dann lief in diesem Fenster gar nichts.
 */
let anlaufMs: number | null = null;
let anlaufRaf = 0;

/**
 * Den Anlauf stoppen: von jetzt an sehen, bis der Kopf sich das erste Mal
 * bewegt. Danach beginnt das Fenster neu -- sonst zählte der Anlauf als
 * zu langsames Abspielen, und genau das war der Fehler.
 */
function stoppeAnlauf(): void {
  cancelAnimationFrame(anlaufRaf);
  anlaufMs = null;
  const start = performance.now();
  const kopfStart = useEditor.getState().playhead;
  const sieh = (): void => {
    const jetzt = performance.now();
    if (useEditor.getState().playhead !== kopfStart) {
      anlaufMs = Math.round(jetzt - start);
      fensterSeit = jetzt;
      kopfSeit = useEditor.getState().playhead;
      return;
    }
    // Nicht ewig: bewegt sich nach acht Sekunden nichts, ist das kein Anlauf
    // mehr, sondern ein Stillstand -- und den zählt `puffer` (7.69o).
    if (jetzt - start > 8000) return;
    anlaufRaf = requestAnimationFrame(sieh);
  };
  anlaufRaf = requestAnimationFrame(sieh);
}

function befund(): Record<string, unknown> {
  const jetzt = performance.now();
  const sekunden = Math.max(0.001, (jetzt - fensterSeit) / 1000);
  const kopfJetzt = useEditor.getState().playhead;
  const flaechen = bildBericht().map((b) => ({
    flaeche: b.flaeche,
    bereit: b.bereit,
    netz: b.netz,
    pausiert: b.pausiert,
    zeit: b.zeit,
    breite: b.breite,
    hoehe: b.hoehe,
    bilder: b.bilder,
    ausgelassen: b.ausgelassen,
    anlaeufe: b.anlaeufe,
    doppelt: b.doppelt,
    suchend: b.suchend,
    suchdauer: b.suchdauer,
    geladenBis: b.geladenBis,
    abgewiesen: b.abgewiesen,
    frei: b.frei,
    grund: b.grund,
    vorab: b.vorab,
    ton: b.ton,
    /** Wie viele Bilder in diesem Fenster dazukamen -- die Zahl, die „läuft“ von „steht“ trennt. */
    bilderNeu: Math.max(0, b.bilder - (bilderSeit[b.flaeche] ?? b.bilder)),
  }));
  const zaehler = bilder();
  const leinwand = GL_GEWUENSCHT ? { ...leinwandBericht(), bildtaktJeSekunde: Math.round((takte.n / sekunden) * 10) / 10 } : null;
  // Wie viel Film in dieser Zeit wirklich vorbeikam: 1,0 heisst Echtzeit.
  // Ab der ersten Bewegung gerechnet, nicht ab dem Tastendruck (7.69v): der
  // Anlauf steht daneben und nicht mehr mittendrin.
  const kopfProSekunde = Math.round(((kopfJetzt - kopfSeit) / sekunden) * 100) / 100;
  for (const b of bildBericht()) bilderSeit[b.flaeche] = b.bilder;
  kopfSeit = kopfJetzt;
  return {
    modus: filmLaeuft() ? 'film' : GL_GEWUENSCHT ? 'grafikkarte' : 'elemente',
    kopfProSekunde,
    /** Millisekunden vom Tastendruck bis zur ersten Bewegung (7.69v). */
    anlaufMs,
    takt: window.__taktgeber?.() ?? null,
    vorgeladen: vorladeStand(),
    // Über die Sitzung gezählt, nicht je Fenster: das Warten *ist* ein
    // Anhalten der Wiedergabe, und das setzte das Fenster jedes Mal neu --
    // die Zahl hätte sich selbst weggeräumt (7.69o).
    puffer: pufferBericht(),
    // Getrennt vom Warten auf Daten: das Warten auf das erste *Bild* (7.69q).
    aufsBild: bildWartenBericht(),
    // Und was im Arbeitsspeicher liegt (7.69s/t): ohne diese Zahl lässt sich
    // ein Warten nicht von „die Datei war gar nicht da“ unterscheiden.
    ram: ramStand(),
    sekunden: Math.round(sekunden * 10) / 10,
    dichte: window.devicePixelRatio || 1,
    fenster: { breite: window.innerWidth, hoehe: window.innerHeight },
    leinwand,
    flaechen,
    zaehler,
    jeSekunde: Object.fromEntries(Object.entries(zaehler).map(([k, v]) => [k, Math.round((v / sekunden) * 10) / 10])),
  };
}

function vonVorn(): void {
  fensterSeit = performance.now();
  kopfSeit = useEditor.getState().playhead;
  bilderSeit = Object.fromEntries(bildBericht().map((b) => [b.flaeche, b.bilder]));
  bilderZuruecksetzen();
  if (GL_GEWUENSCHT) messungZurueck();
  takte.n = 0;
  takte.seit = performance.now();
}

function schicke(projectId: string | null, anlass: 'takt' | 'pause'): void {
  const daten = { anlass, ...befund() };
  vonVorn();
  void api.messwerte({ projectId, ua: navigator.userAgent, daten }).catch(() => undefined);
}

/**
 * Die App hält an, weil Daten fehlen -- **das ist keine Pause** (7.116).
 *
 * Fehlt Material, setzt die Vorschau `playing` auf false und zeigt „Lädt…“
 * (7.69o). Für die Messung sah das bis hierher aus wie ein Anhalten: das
 * Fenster wurde geschlossen, und war es kürzer als eine Sekunde, flog der
 * Befund weg. Beim Weiterlaufen fing alles von vorn an.
 *
 * **Damit erzeugte eine stotternde Wiedergabe gar keine Berichte** -- jedes
 * Stück zu kurz, jedes weggeworfen. Genau die Zahlen, die den schlechten Fall
 * beschreiben, verliessen das Gerät nie, und auf die wartet 7.69v.
 *
 * Also: beim Warten bleibt das Fenster offen. Gemessen wird „wie lief das
 * Abspielen“, und das Warten gehört dazu -- es wird ja eigens gezählt.
 */
let wartetAufDaten = false;

/** Vor dem Anhalten wegen fehlender Daten rufen. */
export function messungWartet(): void {
  wartetAufDaten = true;
}

/** Beim Anspielen rufen. */
export function messungStartet(projectId: string | null): void {
  window.clearInterval(zeitgeber);
  if (wartetAufDaten) {
    // Weiter im selben Fenster: nur der Takt muss wieder anspringen. Kein
    // `vonVorn()`, sonst wäre das Warten aus der Messung heraus.
    wartetAufDaten = false;
    zeitgeber = window.setInterval(() => schicke(projectId, 'takt'), ALLE);
    return;
  }
  laeuftSeit = performance.now();
  vonVorn();
  stoppeAnlauf();
  zeitgeber = window.setInterval(() => schicke(projectId, 'takt'), ALLE);
}

/** Beim Anhalten rufen. Ein kurzes Antippen ist kein Befund. */
export function messungEndet(projectId: string | null): void {
  cancelAnimationFrame(anlaufRaf);
  window.clearInterval(zeitgeber);
  zeitgeber = 0;
  // Wartet die App nur auf Daten, ist das Abspielen nicht zu Ende: Fenster
  // offen lassen, nichts schicken, nichts zurücksetzen (7.116).
  if (wartetAufDaten) return;
  // **Und ein Fenster, in dem gewartet wurde, geht immer hinaus** (7.116).
  //
  // „Ein kurzes Antippen ist kein Befund“ stimmt weiter -- aber das Fenster
  // fängt erst an, wenn der Film wirklich losläuft (`stoppeAnlauf`). Lief er
  // spät los, weil Daten fehlten, ist das Fenster danach kurz, und der Befund
  // flog weg: **genau dann, wenn er am meisten zu sagen hätte.** Am
  // 19. September stand in einem ganzen Durchgang kein einziger Bericht auf
  // dem Server, und das war der Grund.
  //
  // Ein Antippen ohne Anlauf und ohne Warten bleibt still -- dort gibt es
  // nichts zu melden.
  const langGenug = performance.now() - fensterSeit >= MINDESTENS;
  const hatGewartet = (pufferBericht().haltungen ?? 0) > 0;
  if (laeuftSeit && (langGenug || hatGewartet)) schicke(projectId, 'pause');
  laeuftSeit = 0;
}
